import { CheckCircleOutlineRounded, HighlightOffRounded, SendRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getProduct, getProductLeisure, RESPONSE_CHECK_CODE, useCouponQuery } from "../../../../../../api/services";
import { IFormValues } from "../../../../types";
import { ReactQueryKey, RentalType } from "../../../../../../types";
import { FormTextField } from "../../../../../../components/fields";
import { getCarGroupCode } from "../../../../../../utils";
import { useRecoilValue } from "recoil";
import { editPendingBookingRce } from "../../../../recoil";
// todo det disabled e controlli sul code restituito dal rest check (non necessari)
const FormCoupon = () => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    const discountCode = useWatch<IFormValues, 'extraServices.discountCode'>({
        name: 'extraServices.discountCode'
    });
    const pickupLocation = useWatch<IFormValues, 'basicDetails.pickupLocation'>({
        name: 'basicDetails.pickupLocation'
    });
    const dropoffLocation = useWatch<IFormValues, 'basicDetails.dropoffLocation'>({
        name: 'basicDetails.dropoffLocation'
    });
    const pickupDate = useWatch<IFormValues, 'basicDetails.pickupDate'>({
        name: 'basicDetails.pickupDate'
    });

    const dropoffDate = useWatch<IFormValues, 'basicDetails.dropoffDate'>({
        name: 'basicDetails.dropoffDate'
    });

    const carGroup = useWatch<IFormValues, 'basicDetails.carGroup'>({
        name: 'basicDetails.carGroup'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenant" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealer" });

    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}],
        queryFn: () => getProduct({segment: rentalType, businessContinuityInsToolMode: false, tenant: tenant as string, dealer: dealer as string}),
        enabled: false
    });

    const queryClient = useQueryClient();

    const couponQuery = useCouponQuery(
        {
            pickUpDate: pickupDate?.isValid() ? pickupDate.toISOString() : undefined,
            dropoffDate: dropoffDate?.isValid() ? dropoffDate.toISOString() : undefined,
            pickUpLocation: pickupLocation?.id,
            dropOffLocation: dropoffLocation?.id,
            promoCode: discountCode,
            carGroupCode: !!carGroup ? getCarGroupCode(carGroup) : undefined,
            productCodes: !!productsQuery?.data?.products ? productsQuery.data.products.map(elem => {return elem.code}) : undefined,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            enabled: false,
            onSuccess: async (data) => {
                if (data?.code !== RESPONSE_CHECK_CODE.SUCCESS) {
                    formContext.setError('extraServices.discountCode', {
                        message: 'message.validation.invalid_code'
                    });
                    return;
                }
                formContext.clearErrors('extraServices.discountCode');

                const  productList = data?.leisureResult?.productList;

                if(pickupDate?.isValid() && !!discountCode && !!productList) {
                    
                    await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.Product, {segment: RentalType.RETAIL, tenant: tenant as string, dealer: dealer as string}],
                        queryFn: () => getProductLeisure({leisureCode: discountCode, productList, pickUpDate: pickupDate.toISOString(), tenant: tenant as string, dealer: dealer as string})
                    });

                }
            }
        }
    );

    const handleCheck = useCallback(() => {
        couponQuery.refetch();
    }, []);
    const handleKeyDown = useCallback(() => {
        queryClient.resetQueries([ReactQueryKey.Coupon, {tenant: tenant as string, dealer: dealer as string}]);
        if (formContext.getFieldState('extraServices.discountCode').error) {
            formContext.clearErrors('extraServices.discountCode');
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (
            !discountCode ||
            !pickupDate ||
            !dropoffDate ||
            !pickupLocation ||
            !dropoffLocation ||
            !carGroup ||
            !productsQuery?.data?.products
        ) {
            return undefined;
        }
        if (couponQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        if (!couponQuery.data) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleCheck}
                    >
                        <SendRounded />
                    </IconButton>
                </InputAdornment>
            )
        }
        if (couponQuery.data.code !== RESPONSE_CHECK_CODE.SUCCESS) {
            return (
                <HighlightOffRounded
                    sx={{
                        fill: 'red'
                    }}
                />
            );
        }
        return (
            <CheckCircleOutlineRounded
                sx={{
                    fill: 'green'
                }}
            />
        );
    }, [
        discountCode,
        pickupDate,
        dropoffDate,
        pickupLocation,
        dropoffLocation,
        carGroup,
        couponQuery,
        productsQuery?.data?.products
    ]);

    return (
        <FormTextField<IFormValues>
            name="extraServices.discountCode"
            label={t('label.promoCouponLeisureCode') + '*'}
            disabled={isPendingBookingRce || couponQuery.isFetching}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            onKeyDown={handleKeyDown}
        />
    );
}

export default FormCoupon;
